// src/pages/IntCarriers.js
import React, { useEffect, useState, useMemo } from "react"
import { Link, useNavigate, Redirect } from "react-router-dom"

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  ModalFooter,
  Row,
  Spinner,
} from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import axios from "axios"
import apiURl from "url"

const InternationalCarries = () => {
  document.title = "International Carriers"
  const navigate = useNavigate()

  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editID, setEditID] = useState(null)
  const [name, setName] = useState("")
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedCarrierId, setSelectedCarrierId] = useState(null)

  const fetchCarriers = async () => {
    try {
      setIsLoading(true)
      const { data } = await axios.get(`${apiURl}/carriers/list`)
      if (data.status === "success") {
        setRows(data.carriers || [])
      } else {
        toastr.error(data.message || "Failed to fetch carriers")
      }
    } catch (err) {
      toastr.error("Error fetching carriers")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCarriers()
  }, [])

  const resetForm = () => {
    setName("")
    setEditID(null)
    setModalOpen(false)
  }

  const openAdd = () => {
    setEditID(null)
    setName("")
    setModalOpen(true)
  }

  const openEdit = row => {
    setEditID(row.id)
    setName(row.name || "")
    setModalOpen(true)
  }

  const saveCarrier = async e => {
    e.preventDefault()
    if (!name?.trim()) {
      toastr.error("Name is required")
      return
    }

    try {
      const url = editID
        ? `${apiURl}/carriers/update`
        : `${apiURl}/carriers/add`
      const payload = editID ? { id: editID, name } : { name }
      const { data } = await axios.post(url, payload)
      if (data.status === "success") {
        toastr.success(editID ? "Carrier updated" : "Carrier added")
        resetForm()
        fetchCarriers()
      } else {
        toastr.error(data.message || "Failed to save carrier")
      }
    } catch {
      toastr.error("Error saving carrier")
    }
  }

  const handleDelete = () => {
    if (selectedCarrierId) {
      deleteCarrier(selectedCarrierId)
    }
    setShowDeleteModal(false)
  }

  const deleteCarrier = async id => {
    try {
      const { data } = await axios.post(`${apiURl}/carriers/delete`, { id })
      if (data.status === "success") {
        toastr.success("Carrier deleted")
        fetchCarriers()
      } else {
        toastr.error(data.message || "Failed to delete carrier")
      }
    } catch {
      toastr.error("Error deleting carrier")
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Status",
        accessor: "status",
        disableFilters: true,
        Cell: ({ row }) => {
          const { id, status } = row.original
          const isActive = Number(status) === 1
          const handleToggle = async () => {
            const newStatus = isActive ? 0 : 1
            try {
              const { data } = await axios.post(`${apiURl}/carriers/status`, {
                id,
                status: newStatus,
              })
              if (data.status === "success") {
                toastr.success("Status updated successfully")
                fetchCarriers()
              } else {
                toastr.error("Failed to update status")
              }
            } catch (err) {
              toastr.error("Server error")
              console.error(err)
            }
          }

          return (
            <div className="form-check form-switch mb-0">
              <input
                type="checkbox"
                role="switch"
                className="form-check-input"
                id={`carrier-switch-${id}`}
                defaultChecked={isActive}
                onChange={handleToggle}
              />
              <label
                className="form-check-label ms-2 mb-0"
                htmlFor={`carrier-switch-${id}`}
              >
                {isActive ? (
                  <span className="badge rounded-pill bg-success">Active</span>
                ) : (
                  <span className="badge rounded-pill bg-danger">Inactive</span>
                )}
              </label>
            </div>
          )
        },
      },

      {
        Header: "Action",
        accessor: "id",
        disableFilters: true,
        Cell: ({ row, value }) => (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => openEdit(row.original)}
            >
              <i className="bx bx-pencil"></i> Edit
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => {
                setSelectedCarrierId(row.original.id)
                setShowDeleteModal(true)
              }}
            >
              <i className="bx bx-trash"></i> Delete
            </Button>
            <Button
              color="success" // Or 'primary'
              size="sm"
              onClick={() => navigate(`/carriers/${value}/zones`)}
            >
              <i className="bx bx-world"></i> Zones
            </Button>
            <Button
              color="warning" // Or 'primary'
              size="sm"
              onClick={() => navigate(`/carriers/${value}/rates`)}
            >
              <i className="bx bx-dollar"></i> Rates
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <h5 className="mt-2">International Carriers Management</h5>
          </Col>
          <Col className="text-end">
            <Button color="primary" className="btn-sm w-md" onClick={openAdd}>
              + Add Carrier
            </Button>
          </Col>
        </Row>

        <Row className="pt-3">
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center py-4">
                    <Spinner
                      color="primary"
                      style={{ width: "3rem", height: "3rem" }}
                      className="mb-2"
                    />
                    <small className="text-muted">Loading ....</small>
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={rows}
                    customPageSize={10}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      {/* Modal (Name only) */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        centered
      >
        <Form onSubmit={saveCarrier}>
          <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
            {editID ? "Edit Carrier" : "Add Carrier"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Carrier Name *</Label>
              <Input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={resetForm}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editID ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(!showDeleteModal)}
        centered
      >
        <ModalHeader toggle={() => setShowDeleteModal(!showDeleteModal)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this carrier? This action cannot be
          undone.
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowDeleteModal(!showDeleteModal)}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default InternationalCarries
